import React from "react";
import { generatedMaterialsProcessEvidenceLedger } from "../data/generatedMaterialsProcessEvidenceLedger.js";
import { bodies, processes } from "../data/snapshots.js";
import { processBodyFitScore, round } from "../science/kernel.mjs";
import { BodyFitGrid, ProcessFlowChart } from "../components/VisualCharts.jsx";
import { CellStack, DataTable, EvidencePanel, MetricGrid, PageHeader, PillSelector, StackList } from "../components/ui.jsx";

export function ProcessLab({ selectedProcess, selectedProcessId, setSelectedProcessId }) {
  const selectedProcessEvidence =
    generatedMaterialsProcessEvidenceLedger.records.find((record) => record.processId === selectedProcess.id) ?? null;
  const selectedProcessGap =
    generatedMaterialsProcessEvidenceLedger.topProcessGaps.find((gap) => gap.processId === selectedProcess.id) ?? null;
  const sourceRows = selectedProcessEvidence
    ? selectedProcessEvidence.sourceRefs
    : generatedMaterialsProcessEvidenceLedger.sourceFamilyCoverage.map((source) => ({
        sourceId: source.sourceId,
        title: source.sourceId,
        trust: source.present ? "registered-source-family" : "missing",
        sourceType: "ledger-coverage",
        recordHash: source.recordHash ?? "missing",
        queryPolicy: "No selected process evidence record has been generated for this process card yet.",
      }));
  const requiredEvidenceItems = selectedProcessEvidence
    ? [
        ...selectedProcessEvidence.requiredMaterialRecords.map((item) => ({
          id: `material-${item}`,
          title: item,
          meta: "material record",
          body: selectedProcessEvidence.promotionGate.allowedUse,
        })),
        ...selectedProcessEvidence.requiredRuntimeArtifacts.map((item) => ({
          id: `runtime-${item}`,
          title: item,
          meta: "runtime artifact",
          body: selectedProcessEvidence.currentUse,
        })),
      ]
    : [
        {
          id: "missing-process-evidence-record",
          title: "Generated process evidence record missing",
          meta: generatedMaterialsProcessEvidenceLedger.mode,
          body: "Add this process to the materials/process ledger before promoting process, throughput, quality, or cost claims.",
        },
      ];
  const fitRows = bodies.map((body) => {
    const bodyScore = processBodyFit({
      processId: selectedProcess.id,
      bodyId: body.id,
    });
    return {
      body: body.name,
      score: round(bodyScore, 1),
      confidence: bodyScore > 70 ? "high" : bodyScore > 45 ? "medium" : "low",
    };
  });

  return (
    <section className="module">
      <PageHeader
        title="PROCESS LAB"
        muted="Manufacturing math"
        subtitle="Each process card is explicit on maturity, energy demand, feedstock dependency, and fit."
      />
      <PillSelector items={processes} selectedId={selectedProcessId} onSelect={setSelectedProcessId} />

      <div className="module-grid">
        <EvidencePanel
          title={selectedProcess.name}
          status={selectedProcess.uncertainty}
          tone={selectedProcess.uncertainty === "High" ? "red" : "green"}
          className="panel-large"
        >
          <p>{selectedProcess.maturity}</p>
          <MetricGrid
            metrics={[
              { label: "Best fit", value: selectedProcess.bestFit },
              { label: "Maturity", value: selectedProcess.maturity },
              { label: "Uncertainty", value: selectedProcess.uncertainty },
              { label: "Flow steps", value: selectedProcess.flow.length },
              { label: "Evidence score", value: selectedProcessEvidence?.evidenceScore ?? "not generated" },
              { label: "Promotion gate", value: selectedProcessEvidence?.promotionGate.state ?? "missing-record" },
              { label: "Best scenario", value: selectedProcessEvidence?.bestScenarioFit.scenarioName ?? "not generated" },
              { label: "Evidence hash", value: selectedProcessEvidence?.evidenceHash.slice(0, 18) ?? "not generated" },
            ]}
          />
          <ProcessFlowChart steps={selectedProcess.flow} />
        </EvidencePanel>
        <EvidencePanel title="Key parameters" status="table" tone="blue">
          <DataTable
            compact
            columns={[
              { key: "parameter", header: "Parameter", render: ([label]) => label },
              { key: "value", header: "Value", render: ([, value]) => value },
            ]}
            rows={selectedProcess.parameters}
            getRowKey={([label]) => label}
          />
        </EvidencePanel>
      </div>

      <EvidencePanel title="Body fit comparison" status="computed">
        <BodyFitGrid rows={fitRows} />
      </EvidencePanel>

      <div className="module-grid">
        <EvidencePanel
          title="Source-backed process evidence"
          status={selectedProcessEvidence ? selectedProcessEvidence.promotionGate.state : "missing-record"}
          tone={selectedProcessEvidence ? "red" : "amber"}
        >
          <p>{generatedMaterialsProcessEvidenceLedger.operatingRule}</p>
          <DataTable
            compact
            rows={sourceRows}
            getRowKey={(source) => source.sourceId}
            columns={[
              {
                key: "source",
                header: "Source",
                render: (source) => <CellStack title={source.title} meta={source.sourceId} />,
              },
              { key: "trust", header: "Trust" },
              { key: "sourceType", header: "Type" },
              { key: "recordHash", header: "Hash" },
              { key: "queryPolicy", header: "Query policy" },
            ]}
          />
        </EvidencePanel>
        <EvidencePanel
          title="Required proof and experiments"
          status={selectedProcessGap?.priorityLane ?? generatedMaterialsProcessEvidenceLedger.artifactContract.claimState}
          tone="blue"
        >
          <MetricGrid
            metrics={[
              { label: "Ledger mode", value: generatedMaterialsProcessEvidenceLedger.mode },
              { label: "Processes covered", value: generatedMaterialsProcessEvidenceLedger.processCount },
              { label: "Blocked processes", value: generatedMaterialsProcessEvidenceLedger.blockedProcessCount },
              { label: "Ledger hash", value: generatedMaterialsProcessEvidenceLedger.ledgerHash.slice(0, 18) },
              { label: "Selected gap score", value: selectedProcessGap?.evidenceScore ?? "not generated" },
              { label: "Next action", value: selectedProcessGap?.nextAction ?? generatedMaterialsProcessEvidenceLedger.artifactContract.nextAction },
            ]}
            columns={2}
          />
          <StackList
            items={requiredEvidenceItems}
            getTitle={(item) => item.title}
            getMeta={(item) => item.meta}
            getBody={(item) => item.body}
          />
        </EvidencePanel>
      </div>
    </section>
  );
}

function processBodyFit({ processId, bodyId }) {
  const map = {
    "vacuum-sheet": {
      mercury: { vacuumAdvantage: 0.92, energyAvailability: 0.95, thermalPenalty: 0.82, toolingBurden: 0.72, feedstockMatch: 0.54 },
      moon: { vacuumAdvantage: 0.74, energyAvailability: 0.45, thermalPenalty: 0.54, toolingBurden: 0.66, feedstockMatch: 0.74 },
      asteroids: { vacuumAdvantage: 0.86, energyAvailability: 0.82, thermalPenalty: 0.58, toolingBurden: 0.8, feedstockMatch: 0.71 },
      mars: { vacuumAdvantage: 0.6, energyAvailability: 0.3, thermalPenalty: 0.52, toolingBurden: 0.72, feedstockMatch: 0.56 },
      venus: { vacuumAdvantage: 0.58, energyAvailability: 0.86, thermalPenalty: 0.96, toolingBurden: 0.64, feedstockMatch: 0.4 },
    },
    "regolith-sinter": {
      mercury: { vacuumAdvantage: 0.7, energyAvailability: 0.95, thermalPenalty: 0.78, toolingBurden: 0.78, feedstockMatch: 0.74 },
      moon: { vacuumAdvantage: 0.88, energyAvailability: 0.5, thermalPenalty: 0.48, toolingBurden: 0.68, feedstockMatch: 0.94 },
      asteroids: { vacuumAdvantage: 0.9, energyAvailability: 0.72, thermalPenalty: 0.55, toolingBurden: 0.86, feedstockMatch: 0.62 },
      mars: { vacuumAdvantage: 0.62, energyAvailability: 0.32, thermalPenalty: 0.45, toolingBurden: 0.66, feedstockMatch: 0.82 },
      venus: { vacuumAdvantage: 0.5, energyAvailability: 0.84, thermalPenalty: 0.9, toolingBurden: 0.76, feedstockMatch: 0.36 },
    },
    "metal-refining": {
      mercury: { vacuumAdvantage: 0.8, energyAvailability: 0.96, thermalPenalty: 0.74, toolingBurden: 0.92, feedstockMatch: 0.52 },
      moon: { vacuumAdvantage: 0.82, energyAvailability: 0.48, thermalPenalty: 0.5, toolingBurden: 0.86, feedstockMatch: 0.82 },
      asteroids: { vacuumAdvantage: 0.91, energyAvailability: 0.78, thermalPenalty: 0.52, toolingBurden: 0.94, feedstockMatch: 0.88 },
      mars: { vacuumAdvantage: 0.55, energyAvailability: 0.31, thermalPenalty: 0.48, toolingBurden: 0.82, feedstockMatch: 0.68 },
      venus: { vacuumAdvantage: 0.52, energyAvailability: 0.86, thermalPenalty: 0.92, toolingBurden: 0.88, feedstockMatch: 0.46 },
    },
    "spf-db": {
      mercury: { vacuumAdvantage: 0.88, energyAvailability: 0.95, thermalPenalty: 0.66, toolingBurden: 0.8, feedstockMatch: 0.51 },
      moon: { vacuumAdvantage: 0.65, energyAvailability: 0.5, thermalPenalty: 0.36, toolingBurden: 0.71, feedstockMatch: 0.79 },
      asteroids: { vacuumAdvantage: 0.89, energyAvailability: 0.76, thermalPenalty: 0.5, toolingBurden: 0.93, feedstockMatch: 0.68 },
      mars: { vacuumAdvantage: 0.44, energyAvailability: 0.33, thermalPenalty: 0.44, toolingBurden: 0.71, feedstockMatch: 0.62 },
      venus: { vacuumAdvantage: 0.53, energyAvailability: 0.81, thermalPenalty: 0.88, toolingBurden: 0.78, feedstockMatch: 0.39 },
    },
    "oxygen-isru": {
      mercury: { vacuumAdvantage: 0.46, energyAvailability: 0.93, thermalPenalty: 0.7, toolingBurden: 0.7, feedstockMatch: 0.38 },
      moon: { vacuumAdvantage: 0.86, energyAvailability: 0.42, thermalPenalty: 0.55, toolingBurden: 0.78, feedstockMatch: 0.98 },
      asteroids: { vacuumAdvantage: 0.92, energyAvailability: 0.7, thermalPenalty: 0.56, toolingBurden: 0.84, feedstockMatch: 0.6 },
      mars: { vacuumAdvantage: 0.66, energyAvailability: 0.34, thermalPenalty: 0.56, toolingBurden: 0.66, feedstockMatch: 0.96 },
      venus: { vacuumAdvantage: 0.42, energyAvailability: 0.84, thermalPenalty: 0.84, toolingBurden: 0.73, feedstockMatch: 0.31 },
    },
  };

  const bodyMap = map[processId] || map["vacuum-sheet"];
  return processBodyFitScore(bodyMap[bodyId] || {
    vacuumAdvantage: 0.5,
    energyAvailability: 0.5,
    thermalPenalty: 0.5,
    toolingBurden: 0.5,
    feedstockMatch: 0.5,
  });
}
